import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Package, 
  Building2, 
  Briefcase, 
  MessageSquare,
  LogOut,
  Menu,
  X,
  ChevronRight,
  PenLine
} from 'lucide-react';
import { Button } from '../ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/4h4fx5d8_panneau%20logo%20SOPREL%20copie.png";

const sidebarLinks = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { name: 'Pages du site', href: '/admin/pages', icon: PenLine },
  { name: 'Établissements', href: '/admin/content', icon: FileText },
  { name: 'Produits', href: '/admin/products', icon: Package },
  { name: 'Réalisations', href: '/admin/realisations', icon: Building2 },
  { name: 'Offres d\'emploi', href: '/admin/jobs', icon: Briefcase },
  { name: 'Messages', href: '/admin/messages', icon: MessageSquare },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActive = (href) => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-[#0e1c3f] z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="SOPREL" className="h-10 w-auto" />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {sidebarLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'bg-[#0078b7] text-white'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`admin-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Icon className="h-5 w-5" />
                  {link.name}
                </Link>
              );
            })}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-slate-700">
            <div className="px-4 py-2 mb-2">
              <p className="text-white text-sm font-medium">{user?.name}</p>
              <p className="text-slate-400 text-xs">{user?.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Déconnexion
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
                data-testid="admin-mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </button>
              
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm">
                <Link to="/admin" className="text-slate-500 hover:text-slate-700">Admin</Link>
                {location.pathname !== '/admin' && (
                  <>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-900 font-medium">
                      {sidebarLinks.find(l => isActive(l.href))?.name || 'Page'}
                    </span>
                  </>
                )}
              </div>
            </div>

            <Button asChild variant="outline" size="sm" className="hidden sm:flex">
              <Link to="/" target="_blank">Voir le site</Link>
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-3 sm:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
